import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import qs from 'query-string';
import * as api from 'app/api';
import useAuth from 'app/hook/common/useAuth';
import useDs from 'app/hook/common/useDs';
import useCars from 'app/hook/cars/useCars';
import useService from 'app/hook/cars/useService';
import mobileAppBridge from 'app/libs/MobileAppBridge';
import { getLocalItem, setLocalItem } from 'app/libs/storageUtils';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';
import useSsrCookie from 'app/hook/common/useSsrCookie';
import useSsrWindowLocation from 'app/hook/common/useSsrWindowLocation';
import useEnlog from 'app/hook/common/useEnlog';
import debounce from 'lodash/debounce';
import { getRecentViewIds, regRecentViewIds } from '@encarpkg/shared';
import { carDetailImage } from 'app/libs/images';
import dayjs from 'app/libs/dayjs';
import { CAR_OPTION_INFO } from 'app/constants/carOption';
import useMeetgoReservationInfo from 'app/hook/meetgo/useReservation';
import { daBodyName, daPrice, CharactersChange, mileageSection, getCityCodeNumber } from 'app/libs/da';

const ELAN_TOKEN = process.env.REACT_APP_TOKEN_ELAN;
const DIAG_TOKEN = process.env.REACT_APP_TOKEN_DIAG;

export default function useDetailPage() {
	const userAgent = useSsrUserAgent();
	const { getCookie, setCookie } = useSsrCookie();
	const ssrLocation = useSsrWindowLocation();
	const { search } = ssrLocation;
	const query = useMemo(() => qs.parse(search), [search]);
	const viewMode = useMemo(() => {
		if (query.conType === 'mtopc' || (query.conType !== 'pctom' && userAgent().isPc)) return 'pcMode';
		if (query.conType === 'pctom' || (query.conType !== 'mtopc' && !userAgent().isPc)) return 'mobileMode';
		return '';
	}, [query.conType, userAgent]);
	const isPcMode = viewMode === 'pcMode';

	const { user } = useAuth();
	const { setDsClickCode, getDsClickCode } = useDs();
	const { enloggingJsonData } = useEnlog();
	const { base, escrow, detailFlags } = useCars();
	const { isService, isSellType, isSellerType, isEnterFromNextcar, isOriginPrice } = useService();
	const { advertisementType, encarDiagnosisTag, inspectType, isTruck, hasEvBatteryInfo } = detailFlags || {};
	const { detailInfo } = useMeetgoReservationInfo();

	// base
	const baseVehicleId = base.vehicleId;
	const baseVehicleType = base.vehicleType;
	const baseDomestic = base.category?.domestic;
	const baseManufacturerCd = base.category?.manufacturerCd;
	const baseModelCd = base.category?.modelCd;

	const basePrice = base.advertisement?.price;

	const baseUserId = base.contact?.userId;
	const baseLocation = base.contact?.address?.split(' ')[0] || '';
	const baseDummyId = base.manage?.dummyVehicleId;
	const baseAdvertisementStatus = base.advertisement?.status;

	const userId = user?.id;

	/* COMMON */
	// 매물 타입 rtCarType(['dc', 'fc', 'tr']);
	const rtCarType = useCallback(
		(data) => {
			let value;
			if (baseVehicleType === 'CAR') {
				value = baseDomestic ? 0 : 1;
			} else if (baseVehicleType === 'TRUCK') {
				value = 2;
			}
			return data[value];
		},
		[baseVehicleType, baseDomestic],
	);
	// 매물타입코드 - 화물특장:2, 그외:1
	const rtCarTypeCode = useCallback(() => {
		let code = 1;
		if (isTruck) code = 2;
		return code;
	}, [isTruck]);
	// pageId: PC기준으로 페이지아이디를 url파라미터에서 가져옴
	const rtPageId = useCallback(() => query.pageid?.substring(0, 2), [query]);
	// 진단버전체크
	const rtInspectVersion = (ver) => {
		let version = 'V3';
		if (ver === 'V025') version = 'V1';
		if (ver === 'V180701') version = 'V2';
		if (ver === 'V200922') version = 'V3';
		return version;
	};
	// 매물 이름
	const rtCarName = (dataCategory, typeTag) => {
		let result;
		if (dataCategory) {
			const modelName = dataCategory.modelName || '';
			const formDetailName = dataCategory.formDetailName || '';
			const capacityName = dataCategory.capacityName || '';
			const gradeName = dataCategory.gradeName || '';
			const gradeDetailName = dataCategory.gradeDetailName || '';

			const resultText = `${modelName} ${formDetailName} ${capacityName} ${gradeName} ${gradeDetailName}`.trim();
			const resultTag = (
				<>
					<span>{modelName}</span>
					{formDetailName && <span>{formDetailName}</span>}
					{capacityName && <span>{capacityName}</span>}
					{gradeName && <span>{gradeName}</span>}
					{gradeDetailName && <span>{gradeDetailName}</span>}
				</>
			);
			result = typeTag ? resultTag : resultText;
		}
		return result;
	};
	// 엘란 또는 모바일진단에서 차량상세 접근여부 확인 (쿠키비교)
	const isAdministrator = useMemo(() => ELAN_TOKEN === getCookie('ELANTOKEN') || DIAG_TOKEN === getCookie('DIAGTOKEN'), [getCookie]);

	/* 매물 */
	// 조회수 증가
	const regDetailViewCount = useCallback(async (carId) => {
		try {
			await api.regCarsDetailViewCount(carId);
		} catch (err) {
			console.log(err);
		}
	}, []);

	// 지표 수집: 광고효과분석 데이터
	const regAdvertisement = useCallback(async () => {
		const dsClickCode = qs.parse(getDsClickCode());
		const listAdvTypeResult = dsClickCode.listAdvType || query.listAdvType;
		const data = {
			advType: isService.homeService ? 'hs_ad' : '',
			channel: isPcMode ? 'WWW' : 'MOBILE',
			fromPage: listAdvTypeResult || 'etc',
			id: userId || '',
			ip: window.IP_ADDRESS_INFO === 'ip_address_info' ? 'localhost' : window.IP_ADDRESS_INFO,
			pcId: getCookie('PCID') || '',
			vehicleId: baseVehicleId,
			vehicleType: rtCarType(['NATIVE_CAR', 'FOREIGN_CAR', 'TRUCK']),
		};
		try {
			await api.postAdvertisementRegister(data);
		} catch (err) {
			console.log(err);
		}
	}, [query.listAdvType, baseVehicleId, rtCarType, userId, isService.homeService, getDsClickCode, getCookie, isPcMode]);

	// 앱평점리뷰노출조건: 안드로이드 엔카앱
	const isApp = !!userAgent().isApp;
	const isAppRating = isApp && userAgent().isAndroid && userAgent().greaterThanAppVersion('5.5.9');

	// 전화걸기
	const regCall = useCallback(async () => {
		if (isAppRating) {
			// 전화시도 정보
			const getItem = getLocalItem('CAR_DETAIL_CLIENT_CALL');
			const infoData = {
				...getItem,
				totalCount: getItem?.totalCount + 1 || 1,
			};
			setLocalItem('CAR_DETAIL_CLIENT_CALL', infoData);
		}
		// 지표수집 - 인덱스화면의 차량추천으로 사용됨
		const data = {
			channel: isPcMode ? 'WWW' : 'MOBILE',
			id: userId || '',
			ip: window.IP_ADDRESS_INFO === 'ip_address_info' ? 'localhost' : window.IP_ADDRESS_INFO,
			vehicleId: baseVehicleId || detailInfo.vehicleId,
			pcId: getCookie('PCID') || '',
		};

		try {
			await api.postCallIndicatorRegister(data);
		} catch (err) {
			console.log(err);
		}
	}, [baseVehicleId, detailInfo.vehicleId, isAppRating, userId, getCookie, isPcMode]);

	// 지표 수집: 데이터스토리 차량정보
	const rtDsCarInfo = useCallback(() => {
		const carSafeTrade = escrow.existence ? 'Y' : 'N';

		const dataCarInfo = {
			car_type: baseVehicleType,
			car_diag: isService.warranty,
			car_hs: isService.homeServiceDummy ? 'hsad' : isService.homeService,
			car_ew: isService.extendWarranty && baseDomestic && 'A',
			car_brand: isService.brandCertification,
			car_lease: isSellType.lease || false,
			car_rent: isSellType.rent || isSellType.rentSuccession || false,
			car_newprice: isOriginPrice || false,
			car_compen: isService.compensate,
			car_td: isService.testDrive,
			car_safe_trade: isService.homeService ? carSafeTrade : undefined,
		};

		return qs.stringify(dataCarInfo);
	}, [
		baseVehicleType,
		isService.warranty,
		isService.homeServiceDummy,
		isService.homeService,
		isService.brandCertification,
		isService.compensate,
		isService.testDrive,
		isService.extendWarranty,
		baseDomestic,
		isSellType.lease,
		isSellType.rent,
		isSellType.rentSuccession,
		isOriginPrice,
		escrow.existence,
	]);

	// 지표 수집: enlog
	const rtEnlogCarInfo = useCallback(() => {
		const carServiceType = [];
		const service = {
			diag: isService.warranty,
			hs: isService.homeService && !isService.homeServiceDummy,
			hsad: isService.homeService && isService.homeServiceDummy,
			ew: isService.extendWarranty,
			brand: isService.brandCertification,
			dc_brand: isService.brandCertificationDomestic,
			compen: isService.compensate,
			td: isService.testDrive,
			meetgo: isService.meetgo && !isService.meetgoDummy,
			mgad: isService.meetgo && isService.meetgoDummy,
			h2: isService.preDelivery,
			directins: inspectType === 'ENCAR_INSPECT',
			batterypass: hasEvBatteryInfo,
			check: isService.encarCheck,
		};
		Object.keys(service).forEach((v) => {
			if (service[v]) carServiceType.push(v);
		});

		const carSellType = [];
		const sell = {
			lease: isSellType.lease || false,
			rent: isSellType.rentSuccession || false,
			brand_leaserent: isSellType.rent || false,
		};
		Object.keys(sell).forEach((v) => {
			if (sell[v]) carSellType.push(v);
		});

		const adType = [];
		const ad = {
			en_diag: advertisementType === 'AD_DUMMY' && encarDiagnosisTag === 'DIAGNOSIS', // 엔카진단
			en_diag_plus: advertisementType === 'AD_DUMMY' && encarDiagnosisTag === 'DIAGNOSIS_P', // 엔카진단+
			en_diag_double_plus: advertisementType === 'AD_DUMMY' && encarDiagnosisTag === 'DIAGNOSIS_PP', // 엔카진단++
			en_meetgo: advertisementType === 'AD_DUMMY', // 엔카믿고
			diag: advertisementType === 'AD_NORMAL' && encarDiagnosisTag === 'DIAGNOSIS', // 딜러광고-엔카진단,
			diag_plus: advertisementType === 'AD_NORMAL' && encarDiagnosisTag === 'DIAGNOSIS_P', // 딜러광고-엔카진단+
			diag_double_plus: advertisementType === 'AD_NORMAL' && encarDiagnosisTag === 'DIAGNOSIS_PP', // 딜러광고-엔카진단++
			normal: advertisementType === 'AD_NORMAL' && !encarDiagnosisTag, // 일반광고
		};
		Object.keys(ad).forEach((v) => {
			if (ad[v]) adType.push(v);
		});

		const dataCarInfo = {
			ad_type: String(adType),
			cartype: baseVehicleType,
			carservicetype: String(carServiceType),
			carselltype: String(carSellType),
			carid: `${baseVehicleId}`,
			sellerid: baseUserId,
			price: `${basePrice}`,
			status: baseAdvertisementStatus,
			...(advertisementType === 'AD_DUMMY' && { dummyid: baseDummyId }),
			...(query.from_click && { from_click: query.from_click }),
		};
		return dataCarInfo;
	}, [
		isService.warranty,
		isService.homeService,
		isService.homeServiceDummy,
		isService.extendWarranty,
		isService.brandCertification,
		isService.brandCertificationDomestic,
		isService.compensate,
		isService.testDrive,
		isService.meetgo,
		isService.meetgoDummy,
		isService.preDelivery,
		isService.encarCheck,
		inspectType,
		hasEvBatteryInfo,
		isSellType.lease,
		isSellType.rentSuccession,
		isSellType.rent,
		advertisementType,
		encarDiagnosisTag,
		baseVehicleType,
		baseVehicleId,
		baseUserId,
		basePrice,
		baseAdvertisementStatus,
		baseDummyId,
		query.from_click,
	]);

	// 지표 수집: 브레이즈
	const rtBrazeAdtype = useCallback(() => {
		let infoService = String(base.advertisement?.trust) || 'general';

		if (isService.extendWarranty && baseDomestic === false) infoService = infoService.replace('ExtendWarranty', 'ExtendWarrantyImported');
		if (isService.homeServiceDummy) infoService = infoService.replace('HomeService', 'HomeServiceDummy');

		return infoService;
	}, [base.advertisement, isService.homeServiceDummy, isService.extendWarranty, baseDomestic]);
	const rtBrazeViewEvent = useCallback(() => {
		const findPhoto = base.photos.find((v) => v.code === '001');
		const photoUrl = findPhoto?.path ? carDetailImage(findPhoto?.path, findPhoto?.updateDateTime) : '';
		const wantOptionCode = ['010', '075', '085', '032', '058', '022', '034', '077', '087', '068', '095'];
		const isOptionCode = base.options.standard?.filter((optionCode) => wantOptionCode.includes(optionCode));
		const optionsName = isOptionCode.map((optionCode) => CAR_OPTION_INFO.find((n) => n.optionCd === optionCode).optionName);
		const dataCarInfo = {
			carNo: `${base.vehicleNo?.slice(-4)}`,
			carID: `${base.vehicleId}`,
			adtype: `${rtBrazeAdtype()}`,
			firstimg: `${photoUrl}`,
			maker: `${base.category.manufacturerName}`,
			model: `${base.category.modelGroupName}`,
			trim: `${base.category.modelName}`,
			grade: `${base.category.gradeName}`,
			year: `${base.category.formYear}`,
			mileage: `${base.spec.mileage}`,
			region: `${baseLocation}`,
			fuel: `${base.spec.fuelName}`,
			trans: `${base.spec.transmissionName}`,
			color: `${base.spec.colorName}`,
			option: `${optionsName}`,
			seller: `${base.contact.userType}`,
			certiYN: `${base.condition.inspection.formats.length > 0}`,
			insuranceYN: `${base.condition.accident.recordView}`,
		};
		return dataCarInfo;
	}, [
		base.category,
		base.contact,
		base.condition,
		base.spec,
		base.photos,
		base.options,
		base.vehicleId,
		base.vehicleNo,
		baseLocation,
		rtBrazeAdtype,
	]);
	const rtBrazeViewAttribute = useCallback(() => {
		const data = [
			`date_${dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss')}`,
			`carNo_${base.vehicleNo?.slice(-4)}/${base.category.modelGroupName}/${base.category.modelName}`,
			`certi_${base.condition.inspection.formats.length > 0 ? 'Y' : 'N'}`,
			`insurance_${base.condition.accident.recordView ? 'Y' : 'N'}`,
			`carID_${base.vehicleId}`,
		];
		return data;
	}, [base.category, base.condition, base.vehicleId, base.vehicleNo]);
	const rtBrazeClickEvent = useCallback(() => {
		const dataCarInfo = {
			carNo: `${base.vehicleNo?.slice(-4)}`,
			model: `${base.category.modelGroupName}`,
			trim: `${base.category.modelName}`,
			carID: `${base.vehicleId}`,
			adtype: `${rtBrazeAdtype()}`,
			price: `${base.advertisement.price}`,
		};
		return dataCarInfo;
	}, [base.advertisement, base.category, base.vehicleId, base.vehicleNo, rtBrazeAdtype]);
	const rtBrazeClickAttribute = useCallback(() => {
		const data = [
			`date_${dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss')}`,
			`carNo_${base.vehicleNo?.slice(-4)}/${base.category.modelGroupName}/${base.category.modelName}`,
			`adtype_${rtBrazeAdtype()}`,
			`price_${base.advertisement.price}`,
			`carID_${base.vehicleId}`,
		];
		return data;
	}, [base.advertisement, base.category, base.vehicleId, base.vehicleNo, rtBrazeAdtype]);

	const regDataStoryCarInfo = useCallback(() => {
		if (baseVehicleType) {
			setDsClickCode(rtDsCarInfo());
		}
	}, [setDsClickCode, baseVehicleType, rtDsCarInfo]);

	// 클린엔카 저렴한 매물 알림을 위한 쿠키 설정 (검색결과에서 사용)
	const getUnclean = useCallback(async () => {
		try {
			const { data } = await api.getUncleanCheck(baseVehicleId);
			if (!data.cleaned) {
				const getData = getCookie('uncleanCars');
				if (getData.indexOf(baseVehicleId) < 0) {
					const stringData = `${getData}${getData && ','}${baseVehicleId}`;
					setCookie('uncleanCars', stringData, 1);
				}
			}
		} catch (err) {
			console.log(err);
		}
	}, [baseVehicleId, getCookie, setCookie]);

	/*
		사용자가 매물을 조회했던 카운트를 저장하여, 이 차량을 3번방문했을때 5번방문했을때 등의 로직에 활용합니다
	*/
	// 클라이언트 매물 조회수: delete
	const localCarViewCountDelete = useCallback(() => {
		const getItem = getLocalItem('ACCESS_COUNT_CARID');
		const arrayItem = Object.entries(getItem);
		const arrayTime = arrayItem.map((v) => v[1][1]);
		const minTime = arrayTime.indexOf(Math.min(...arrayTime));

		delete getItem[arrayItem[minTime][0]];
		setLocalItem('ACCESS_COUNT_CARID', getItem);
	}, []);

	// 클라이언트 매물 조회수: update
	const localCarViewUpdate = useCallback(
		(carId) => {
			if (carId) {
				const getItem = getLocalItem('ACCESS_COUNT_CARID');
				const count = getItem && getItem[carId] ? Number(getItem[carId][0]) + 1 : 1;
				const obj = {
					...getItem,
					[carId]: [count, new Date().getTime()],
				};
				setLocalItem('ACCESS_COUNT_CARID', obj);

				if (getItem && Object.keys(getItem).length > 100) localCarViewCountDelete();
			}
		},
		[localCarViewCountDelete],
	);

	// 클라이언트 매물 조회수: get
	const localCarViewCount = useCallback((carId) => {
		const getItem = getLocalItem('ACCESS_COUNT_CARID');
		return getItem?.[carId]?.[0];
	}, []);

	const getRecommendCarsId = useCallback(async () => {
		try {
			const { data } = await api.getRecommendCars(baseVehicleId);
			const carIds = data.carIds.toString();
			return carIds;
		} catch (err) {
			console.log(err);
			return [];
		}
	}, [baseVehicleId]);

	const getRecentViewExcludingCurrent = useCallback(
		(carId, limit) => {
			const { viewedCarIds, viewedTruckIds } = getRecentViewIds();
			const viewedIds = baseVehicleType === 'TRUCK' ? viewedTruckIds : viewedCarIds;
			return viewedIds
				.filter((v) => String(v) !== String(carId))
				.slice(0, limit)
				.toString();
		},
		[baseVehicleType],
	);

	const regRecentView = useCallback(
		(carId) => {
			regRecentViewIds(carId, baseVehicleType);
		},
		[baseVehicleType],
	);

	// 중고차론 팝업 노출 조건
	const loanLayerViewType = useCallback(() => {
		let loanType;
		const hasAccessedLoanConsult = getCookie('hasAccessedLoanConsult');
		const loanCountNum = baseVehicleId && localCarViewCount(baseVehicleId) === 3;
		// 올 뉴 카니발, 아반떼 AD, 그랜저 HG, E-클래스 W213, 올 뉴 K7
		const specificCarModelArray = ['002113', '001136', '001106', '013072', '002122'];
		const carModel = baseManufacturerCd + baseModelCd;
		const isSpecificCarModel = specificCarModelArray.some((item) => item === carModel);

		const isVisibleLoanLayer = !isSellType.leaseRent && !isEnterFromNextcar && !!hasAccessedLoanConsult && loanCountNum;
		if (isVisibleLoanLayer && isSpecificCarModel) {
			loanType = 'loanModel';
		} else if (isVisibleLoanLayer && basePrice < 2500) {
			loanType = 'loanPrice';
		}
		return loanType;
	}, [isSellType.leaseRent, baseManufacturerCd, baseModelCd, basePrice, localCarViewCount, baseVehicleId, isEnterFromNextcar, getCookie]);

	// 채팅문의
	const inquiryType = useCallback(
		(appWtHit) => {
			const contactNumber = base.contact.no.replace(/-/gi, '');
			// mobileAppBridge().getAppCarInfo() 호출 => 앱실행됨 => 앱에서 아래 3개의 함수실행됨
			window.appLogCarInfo = () => rtEnlogCarInfo();
			window.appLogCarInfoBrazeEvent = () => rtBrazeClickEvent();
			window.appLogCarInfoBrazeAttribute = () => rtBrazeClickAttribute();
			mobileAppBridge().getAppCarInfo(base.vehicleId, base.contact.userId, contactNumber, appWtHit);
		},
		[base.contact, base.vehicleId, rtEnlogCarInfo, rtBrazeClickEvent, rtBrazeClickAttribute],
	);

	// 차량상세 스크롤지표전송
	const sendDataTalkScrollImpression = useCallback(
		(impressions) => {
			const dataTalkCustom = {
				eventtype: 'ACTION',
				eventname: '컨텐츠노출',
				param: { impression: impressions.toString() },
			};
			enloggingJsonData({ dataTalkCustom });
		},
		[enloggingJsonData],
	);

	const debouncedSendDataTalkScrollImpression = useRef(debounce(sendDataTalkScrollImpression, 300));
	useEffect(() => {
		debouncedSendDataTalkScrollImpression.current = debounce(sendDataTalkScrollImpression, 300);
		return debouncedSendDataTalkScrollImpression.current.cancel;
	}, [sendDataTalkScrollImpression]);

	const daBannerParam = useCallback(() => {
		// 준신차 (1년이 안지난 차량, 주행거리 10000 이하)
		const nearNew = () => {
			const year = base.category?.yearMonth;
			const checkYear = Number(year) + 100 > Number(dayjs(new Date()).format('YYYYMM'));
			const checkMileage = base.spec?.mileage <= 10000;
			return checkYear && checkMileage;
		};
		const sellerTypeText = () => {
			if (isSellerType.isIndividaul) return 'individual';
			if (isSellerType.isApprovedRent) return 'rent';
			if (isSellerType.isApprovedLease) return 'lease';
			if (isSellerType.isBrand) return 'brand';
			if (isSellerType.isDealer || isSellerType.isRepeat) return 'dealer';
			return '';
		};
		// 우선순위 - 엔카믿고 > 엔카보증 > 엔카진단 > 헛걸음보상
		const serviceType = () => {
			if (isService.encarMeetGo) return 'EncarMeetGo';
			if (isService.extendWarranty) return 'ExtendWarranty';
			if (isService.encarDiagnosis) return 'EncarDiagnosis';
			if (isService.compensate) return 'Compensate';
			return 'none';
		};
		const sellType = () => {
			if (isSellType.normal) return 'general';
			if (isSellType.leaseOperating) return 'lease_op';
			if (isSellType.leaseFinancing) return 'lease_fi';
			if (isSellType.rent || isSellType.rentSuccession) return 'rent';
			return '';
		};
		const manufacturer = CharactersChange(base.category?.manufacturerEnglishName);
		const modelGroup = CharactersChange(base.category?.modelGroupEnglishName);
		const year = base.category?.yearMonth ? base.category?.yearMonth.slice(0, 4) : '';
		const fullName = `detail_${manufacturer}_${modelGroup}`;
		const city = base.contact?.address ? getCityCodeNumber(base.contact.address.split(' ')[0]) : '';
		const diagnosis = isService.warranty ? 'Y' : 'N';
		// 현재 월 기준 연식(YYYYMM)이 23개월 이내(해당 월 포함)면 Y — 예: 2026-03 기준 최소 2024-04
		const rentYearYmCutoff = dayjs().startOf('month').subtract(23, 'month').format('YYYYMM');
		const carYearMonth = base.category?.yearMonth ? String(base.category.yearMonth).slice(0, 6) : '';
		const rent_year = carYearMonth.length === 6 && Number(carYearMonth) >= Number(rentYearYmCutoff) ? 'Y' : 'N';

		const params = {
			mobile: {
				m_company: `detail_${manufacturer}`,
				m_modelGroup: fullName,
				[`m_detail_${daBodyName(base.spec?.bodyName)}`]: 'checked',
				m_price: daPrice(base.advertisement?.price),
				m_detail_fuel: base.spec?.fuelCd,
				m_seller: sellerTypeText(),
				m_salesmethod: sellType(),
				m_pageid: rtCarType(['dc', 'fc', 'tr']),
				m_year: year,
				m_mileage: mileageSection(base.spec?.mileage),
				m_service: serviceType(),
				m_nearnew: nearNew() ? fullName : '',
			},
			pc: {
				company: `detail_${manufacturer}`,
				modelGroup: fullName,
				[`detail_${daBodyName(base.spec?.bodyName)}`]: 'checked',
				price: daPrice(base.advertisement?.price),
				fuel_detail: base.spec?.fuelCd,
				seller: sellerTypeText(),
				salesmethod: sellType(),
				pageid: rtPageId() ? rtPageId() : rtCarType(['dc', 'fc', 'tr']),
				year,
				mileage: mileageSection(base.spec?.mileage),
				service: serviceType(),
				city,
				diagnosis,
				rent_year,
			},
		};
		return params;
	}, [base.category, base.spec, base.advertisement, base.contact, isSellerType, isService, isSellType, rtPageId, rtCarType]);

	return {
		viewMode,
		baseLocation,
		rtCarName,
		rtCarType,
		rtCarTypeCode,
		rtPageId,
		rtInspectVersion,
		isAdministrator,
		rtDsCarInfo,
		rtEnlogCarInfo,
		rtBrazeViewEvent,
		rtBrazeViewAttribute,
		rtBrazeClickEvent,
		rtBrazeClickAttribute,
		regDetailViewCount,
		regAdvertisement,
		regCall,
		regDataStoryCarInfo,
		getUnclean,
		localCarViewUpdate,
		localCarViewCount,
		loanLayerViewType,
		inquiryType,

		getRecommendCarsId,
		getRecentViewExcludingCurrent,
		regRecentView,
		sendDataTalkScrollImpression: debouncedSendDataTalkScrollImpression.current,
		daBannerParam,
	};
}
